import { lazy, useCallback, useEffect, useRef, useState } from 'react';
import { useLoaderData } from 'react-router-dom';
import NoSleep from 'nosleep.js';
import { getPolls } from '@/apis';
import { socket } from '@/services/socket';
import socketEvents from '@/utils/socketEvents';
import useStore from '@/store/CreateStore';
import ErrorBoundary from '@/components/ErrorBoundary';
import { exitFullscreen } from '@/utils/common';
import { classLoaderData } from '@/pages/interface';
import VideoTile from '../components/Whiteboard/VideoTile';
import ManageBoards from '@/components/ManageBoards';
import FloatingSidebar from '@/components/Sidebar/FloatingSidebar';

const Toolbar = lazy(() => import('@/components/Toolbar'));
const Modal = lazy(() => import('@/components/Modal'));
const Button = lazy(() => import('@/components/Button'));
const Boards = lazy(() => import('@/components/Boards'));

const SocketManager = lazy(() => import('@/components/SocketManager'));
const TeacherHeader = lazy(() => import('@/components/TeacherHeader'));
const WaitingRoom = lazy(() => import('@/components/WaitingRoom'));
const DisconnectRoom = lazy(() => import('@/components/DisconnectRoom'));

const WhiteBoard = () => {
  const {
    success,
    error: initialError,
    wait,
    message,
    startTime = 0,
    endTime,
    classParams,
    roomId = 0,
    userId = 0,
    presenterId = 0,
    isRecorder,
    isRecording
  } = useLoaderData() as classLoaderData;

  const [agoraReady, setAgoraReady] = useState(userId === presenterId);
  const [open, setOpen] = useState(false);
  const [networkModal, setNetworkModal] = useState(false);

  const error = useStore((state) => state.room.error);
  const sidebar = useStore((state) => state.room.sidebar);
  const forcedDisconnect = useStore((state) => state.room.forcedDisconnect);
  const polls = useStore((state) => state.poll.polls);
  const setRoomData = useStore((state) => state.room.setRoomData);
  const setPolls = useStore((state) => state.poll.setPolls);

  const noSleepRef = useRef<NoSleep | null>(null);
  const fetching = useRef(false);

  const roomStart = useCallback(() => {
    socket.emit(socketEvents.room.START, {
      roomId,
      userId
    });
    setOpen(false);
  }, [roomId, userId]);

  useEffect(() => {
    setRoomData({ roomId, userId, presenterId, isRecorder, startTime, endTime, isRecording });
  }, [
    roomId,
    presenterId,
    userId,
    setRoomData,
    success,
    startTime,
    endTime,
    isRecorder,
    isRecording
  ]);

  useEffect(() => {
    try {
      noSleepRef.current = new NoSleep();
    } catch (error) {
      console.error(error);
    }
    if (noSleepRef.current) {
      noSleepRef.current.enable();
    }
    return () => {
      if (noSleepRef.current) {
        noSleepRef.current.disable();
      }
    };
  }, []);

  useEffect(() => {
    async function fetchPolls() {
      try {
        const response = await getPolls(roomId);

        if (response?.data && Array.isArray(response.data)) {
          setPolls(response.data);
        }
      } catch (error) {
        console.error('Error fetching polls:', error);
        setPolls([]);
      }
      fetching.current = false;
    }

    if (!polls && !fetching.current) {
      fetching.current = true;
      fetchPolls();
    }
  }, [roomId, setPolls, polls]);

  useEffect(() => {
    const goOffline = () => {
      setNetworkModal(true);
    };

    window.addEventListener('offline', goOffline);
    return () => {
      window.removeEventListener('offline', goOffline);
    };
  }, [setRoomData]);

  useEffect(() => {
    return () => {
      if (document.fullscreenElement) {
        exitFullscreen();
      }
      if (
        typeof screen.orientation === 'object' &&
        typeof screen.orientation.unlock === 'function'
      ) {
        screen.orientation.unlock();
      }
    };
  }, []);

  if (wait) return <WaitingRoom classParams={classParams} />;
  if (forcedDisconnect) return <DisconnectRoom />;
  if (error || initialError)
    return (
      <ErrorBoundary
        classError={
          initialError
            ? message === 'ENDED'
              ? 'The class has ended'
              : message === 'Room not found'
                ? "The class doesn't exist"
                : undefined
            : undefined
        }
      />
    );
  return (
    <>
      <Modal
        label="Oops! Network Glitch"
        isOpen={networkModal}
        crossVisible={false}
        overlayClick={false}
      >
        <div className="w-[40svw]">
          Your internet seems unstable. Please check it and reload the page to continue.
          <div className="flex justify-end p-2">
            <Button variant="primary" onClick={() => location.reload()}>
              Reload
            </Button>
          </div>
        </div>
      </Modal>
      {userId === presenterId && (
        <Modal label="Go Live" isOpen={open} crossVisible={false} overlayClick={false}>
          <div className="w-[40svw]">
            It is time for your class to start. Have a good one.
            <div className="flex justify-end p-2">
              <Button variant="primary" onClick={roomStart}>
                Enter Live Session
              </Button>
            </div>
          </div>
        </Modal>
      )}
      {success && (
        <SocketManager
          setNetworkModal={setNetworkModal}
          setOpen={setOpen}
          setAgoraReady={setAgoraReady}
        />
      )}
      {agoraReady && (
        <>
          <div className="flex h-dvh">
            <div className="relative flex flex-1 flex-col overflow-hidden">
              {userId === presenterId ? (
                <>
                  <TeacherHeader />
                  <div className="relative flex">
                    <Toolbar />
                    <Boards />
                    <FloatingSidebar isOpen={sidebar === 'boards'}>
                      <div className="thin-scrollbar my-1 max-h-[380px] overflow-auto">
                        <ManageBoards />
                      </div>
                    </FloatingSidebar>
                  </div>
                </>
              ) : (
                <>
                  <div className="overflow-hidden portrait:aspect-video portrait:h-auto portrait:w-dvw">
                    <Boards />
                  </div>
                </>
              )}
              <div className="pointer-events-none absolute right-0.5 bottom-0.5 w-1/5 landscape:w-1/5 landscape:md:max-w-1/5">
                <div className="aspect-video w-full">
                  <VideoTile />
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </>
  );
};

export default WhiteBoard;
